import { useState, useMemo } from "react";
/**
 * Onboarding shared state, catalogues, and display primitives.
 * Converted from design prototype shared.jsx → TypeScript.
 */

// ── Step definitions ────────────────────────────────────────────────────────

export interface StepDef {
  id: string;
  title: string;
  short: boolean;
}

export const ALL_STEPS: StepDef[] = [
  { id: "welcome", title: "Welcome",     short: true  },
  { id: "topic",   title: "First topic",  short: true  },
  { id: "format",  title: "Format",       short: false },
  { id: "niche",   title: "Niche",        short: true  },
  { id: "channel", title: "Channel",      short: true  },
  { id: "voice",   title: "Voice",        short: false },
  { id: "youtube", title: "Publish",      short: false },
  { id: "kickoff", title: "Ship it",      short: true  },
];

export function getStepsFor(short: boolean): StepDef[] {
  return short ? ALL_STEPS.filter(s => s.short) : ALL_STEPS;
}

// ── State hook ──────────────────────────────────────────────────────────────

export interface OnboardingState {
  topic: string;
  format: "long" | "short";
  niche: string | null;
  channelName: string;
  tone: "casual" | "balanced" | "formal";
  voice: string | null;
  youtubeConnected: boolean;
}

type SetFn = (key: keyof OnboardingState, value: any) => void;

export function useOnboarding(initial: Partial<OnboardingState> = {}): [OnboardingState, SetFn] {
  const [state, setState] = useState<OnboardingState>({
    topic: "",
    format: "long",
    niche: null,
    channelName: "",
    tone: "balanced",
    voice: null,
    youtubeConnected: false,
    ...initial,
  });
  const set: SetFn = (k, v) => setState(s => ({ ...s, [k]: v }));
  return [state, set];
}

// ── Catalogues ──────────────────────────────────────────────────────────────

export interface Niche { id: string; name: string; blurb: string; tags: string[]; }
export const NICHES: Niche[] = [
  { id: "reddit",  name: "Reddit Stories",    blurb: "AITA, relationships, viral threads.", tags: ["narrative","drama"] },
  { id: "top10",   name: "Top 10 Lists",       blurb: "Countdown rankings on niche topics.", tags: ["listicle","facts"] },
  { id: "history", name: "History Explainers", blurb: "Long-form deep dives, cinematic.",    tags: ["explainer","long"] },
  { id: "motiv",   name: "Motivational",       blurb: "Stoic, hustle, mindset content.",     tags: ["short","loop"] },
  { id: "crime",   name: "Crime & Mystery",    blurb: "True crime, unsolved cases.",         tags: ["narrative","dark"] },
  { id: "facts",   name: "Curious Facts",      blurb: "Did-you-know science, nature, space.", tags: ["explainer","short"] },
];

export interface Voice { id: string; name: string; accent: string; gender: string; style: string; }
export const VOICES: Voice[] = [
  { id: "marcus", name: "Marcus", accent: "American", gender: "M", style: "Cinematic narrator" },
  { id: "ava",    name: "Ava",    accent: "American", gender: "F", style: "Warm, storytelling" },
  { id: "rhys",   name: "Rhys",   accent: "British",  gender: "M", style: "Documentary, dry" },
  { id: "noor",   name: "Noor",   accent: "Neutral",  gender: "F", style: "Crisp, educational" },
  { id: "kai",    name: "Kai",    accent: "American", gender: "M", style: "High-energy, fast" },
  { id: "elena",  name: "Elena",  accent: "European", gender: "F", style: "Sultry, mysterious" },
];

export const TOPIC_EXAMPLES = [
  "Why monks lived in caves for years",
  "Top 10 weirdest deep-sea creatures",
  "The Soviet space dog program, explained",
  "AITA for ghosting my best friend?",
  "3 stoic habits that changed my life",
];

export interface Tone { id: string; label: string; blurb: string; }
export const TONES: Tone[] = [
  { id: "casual",   label: "Casual",   blurb: "Conversational, friendly, like a podcast." },
  { id: "balanced", label: "Balanced", blurb: "Clean and professional. The safe choice." },
  { id: "formal",   label: "Formal",   blurb: "Documentary-grade. Authoritative." },
];

// ── Display primitives ──────────────────────────────────────────────────────

export function TFLogo({ size = 22 }: { size?: number }) {
  return (
    <span className="logo" style={{ fontSize: size <= 22 ? 15 : 17 }}>
      <span className="mark" style={{ width: size, height: size, fontSize: size * 0.5 }}>
        <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 10 10"><polygon points="2,1 2,9 9,5" fill="currentColor" /></svg>
      </span>
      TubeForge
    </span>
  );
}

export function StepCounter({ current, total, mode = "dots" }: { current: number; total: number; mode?: "dots" | "bar" | "text" }) {
  if (mode === "bar") {
    const pct = ((current + 1) / total) * 100;
    return <div className="progress" style={{ width: "100%" }}><div style={{ width: `${pct}%` }} /></div>;
  }
  if (mode === "text") {
    return (
      <div className="label" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span>Step {current + 1} of {total}</span>
        <div style={{ flex: 1, height: 1, background: "var(--rule)" }} />
      </div>
    );
  }
  return (
    <div className="step-dots">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={"dot " + (i < current ? "done" : i === current ? "current" : "")} />
      ))}
    </div>
  );
}

export function ChoicesSummary({ state, compact = false }: { state: OnboardingState; compact?: boolean }) {
  const items = [
    state.topic && { l: "Topic", v: state.topic.length > 38 ? state.topic.slice(0, 38) + "…" : state.topic },
    state.format && { l: "Format", v: state.format === "long" ? "Long-form · 16:9" : "Shorts · 9:16" },
    state.niche && { l: "Niche", v: NICHES.find(n => n.id === state.niche)?.name },
    state.channelName && { l: "Channel", v: state.channelName },
    state.voice && { l: "Voice", v: VOICES.find(v => v.id === state.voice)?.name },
    state.youtubeConnected && { l: "YouTube", v: "Connected" },
  ].filter(Boolean) as Array<{ l: string; v: string | undefined }>;

  if (!items.length) return <div className="muted" style={{ fontSize: 13 }}>Your picks will appear here.</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: compact ? 10 : 14 }}>
      {items.map((item, idx) => (
        <div key={idx} className="anim-in" style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span className="label" style={{ fontSize: 10 }}>{item.l}</span>
          <span style={{ fontSize: compact ? 13 : 14, color: "var(--fg)" }}>{item.v}</span>
        </div>
      ))}
    </div>
  );
}

export function VoiceWave({ seed = 1, width = 120, height = 24, playing = false }: { seed?: number; width?: number; height?: number; playing?: boolean }) {
  const rng = (i: number) => { const x = Math.sin(i * 9.7 + seed * 3.1) * 10000; return Math.abs(x - Math.floor(x)); };
  const bars = 20;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${bars * 4} ${height}`}>
      {Array.from({ length: bars }).map((_, i) => {
        const h = 4 + rng(i) * (height - 6);
        return <rect key={i} x={i * 4 + 1} y={(height - h) / 2} width="2.5" height={h} rx="1" fill={playing ? "var(--accent)" : "currentColor"} opacity={playing ? 1 : 0.5} />;
      })}
    </svg>
  );
}

export function Confetti({ count = 60 }: { count?: number }) {
  const pieces = useMemo(() => Array.from({ length: count }).map((_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.4,
    color: ["#4f46e5", "#818cf8", "#10b981", "#f59e0b", "#ef4444"][i % 5],
    rot: Math.random() * 360,
  })), [count]);
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 10 }}>
      {pieces.map((p, i) => (
        <div key={i} className="confetti-piece" style={{
          left: `${p.left}%`,
          background: p.color,
          animationDelay: `${p.delay}s`,
          transform: `rotate(${p.rot}deg)`,
        }} />
      ))}
    </div>
  );
}
